import { Formik } from 'formik';
import React, { useEffect, useRef, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { useEventHub } from '@pwc/event-hub-ui';
import { useSections } from '../../../common/components/SectionLayout/SectionContext';
import {
  viewHypothesisPath,
  reportPreviewPath,
} from '../../../common/services/urlService';
import { ENTITY_TYPES } from '../../../common/constants/General';
import { getLinksFromNarratives } from '../../narratives/narrativesUtilities';
import HypothesisForm from '../common/HypothesisForm';
import { emptyHypothesis, hypothesisSchema } from '../common/hypothesisModel';
import { postKfa } from '../kfaService';

import './create-hypothesis.scss';
import { getAlertConfigs } from '../../../common/components/Alert/AlertConfigs';
import { showBannerError } from '../../../common/redux/errors/errorsSlice';

const parentContentId = -1;

const CreateHypothesis = ({ dispatch }) => {
  const history = useHistory();
  const { projectId } = useParams();
  const formikRef = useRef();
  const [connectedContentIsChanged, setConnectedContentIsChanged] =
    useState(false);

  const [errorType, setErrorType] = useState();
  const [isSaving, setIsSaving] = useState(false);
  const [defaultHypothesis] = useState(emptyHypothesis());
  const [currentlyVisibleConnections, setCurrentlyVisibleConnections] =
    useState(null);
  const [thumbnail, setThumbnail] = useState();
  const { eventHub } = useEventHub();

  const setConnectedContentChanged = () => {
    setConnectedContentIsChanged(!connectedContentIsChanged);
  };

  useEffect(() => {
    if (formikRef.current) {
      formikRef.current.resetForm();
      formikRef.current.validateForm(defaultHypothesis);
    }
  }, [history.location, formikRef, defaultHypothesis]);

  const updateThumbnail = (newThumbnail) => {
    setThumbnail(newThumbnail);
  };

  const cancelAndGoBack = () => history.goBack();

  const gotoHypothesisView = (contentId) => {
    history.replace(viewHypothesisPath(projectId, contentId));
  };

  const [{ currentSections = [] }] = useSections();

  const submitKfa = async (kfaFormData) => {
    const links = getLinksFromNarratives(
      currentlyVisibleConnections,
      parentContentId
    );
    return postKfa(projectId, {
      ...kfaFormData,
      sections: currentSections.map((section) => ({
        ...section,
        items: section.items.map((item) => ({
          ...item,
          column: item.analysisColumn,
        })),
      })),
      title: kfaFormData.title.trim(),
      body: kfaFormData.body.trim(),
      insight: {
        description: kfaFormData.insight.trim(),
        order: 0,
        layoutType: 1,
        items: thumbnail
          ? [{ ...thumbnail, order: 0, childLibraryItems: [] }]
          : [],
      },
      tags: kfaFormData.tags.map((tag) => ({
        text: tag,
      })),
      links,
    });
  };

  const handlePublish = async (kfaFormData) => {
    setIsSaving(true);
    try {
      const contentId = await submitKfa(kfaFormData);
      setErrorType();
      gotoHypothesisView(contentId);
      eventHub?.publish('comment-reload-comment-threads', {});
    } catch (e) {
      setIsSaving(false);
      const errorMessage = getAlertConfigs(e);
      dispatch(showBannerError(errorMessage.message));
      setErrorType(e);
    }
  };

  const previewAction = async (values) => {
    setIsSaving(true);
    try {
      const contentId = await submitKfa(values);
      setErrorType();
      history.replace(
        reportPreviewPath(
          projectId,
          contentId,
          'latest',
          true,
          ENTITY_TYPES.KFA
        )
      );
    } catch (e) {
      setIsSaving(false);
      setErrorType(e);
      const errorMessage = getAlertConfigs(e);
      dispatch(showBannerError(errorMessage.message));
    }
  };

  return (
    <Formik
      initialValues={{
        ...defaultHypothesis,
      }}
      enableReinitialize
      onSubmit={handlePublish}
      validationSchema={hypothesisSchema}
      validateOnMount
      validateOnChange
      innerRef={formikRef}
    >
      {({ errors, touched, isValid, values, dirty, submitForm }) => (
        <HypothesisForm
          onCancel={cancelAndGoBack}
          isSaving={isSaving}
          errorType={errorType}
          errors={errors}
          touched={touched}
          isValid={isValid}
          dirty={dirty || values.title}
          projectId={projectId}
          parentContentId={parentContentId}
          currentlyVisibleConnections={currentlyVisibleConnections}
          setCurrentlyVisibleConnections={setCurrentlyVisibleConnections}
          updateThumbnail={updateThumbnail}
          setConnectedContentChanged={setConnectedContentChanged}
          thumbnail={thumbnail}
          previewAction={() => previewAction(values)}
          submitForm={submitForm}
          values={values}
          isCreateMode
        />
      )}
    </Formik>
  );
};

export default CreateHypothesis;
